import type {
  NormalizedWishlist,
  WishlistImageApi,
  WishlistItem,
  WishlistResponseApi,
  WishlistToggleResponseApi,
} from "./types";
import { formatDiscountPercentage } from "@/src/lib/utils/discount";

const PRODUCT_FALLBACK_IMAGE = "/Office%20Furniture/image-removebg-preview.png";

function parsePrice(value: string | number | null | undefined) {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return 0;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildAuthHeaders(accessToken?: string | null) {
  const headers: Record<string, string> = {};

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

function buildMediaUrl(path: string) {
  if (!path) {
    return PRODUCT_FALLBACK_IMAGE;
  }

  if (path.startsWith("http://") || path.startsWith("https://")) {
    const url = new URL(path);
    url.pathname = url.pathname.replace(/\/{2,}/g, "/");
    return url.toString();
  }

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;

  if (!apiBaseUrl) {
    return path;
  }

  const mediaBaseUrl = apiBaseUrl.endsWith("/api")
    ? apiBaseUrl.slice(0, -4)
    : apiBaseUrl.replace(/\/api\/?$/, "");

  const normalizedPath = `/${path}`.replace(/\/{2,}/g, "/");
  return `${mediaBaseUrl}${normalizedPath}`;
}

function getSortedImages(images: WishlistImageApi[]) {
  return [...images].sort((firstImage, secondImage) => {
    if (firstImage.order !== secondImage.order) {
      return firstImage.order - secondImage.order;
    }

    if (firstImage.is_primary === secondImage.is_primary) {
      return firstImage.id - secondImage.id;
    }

    return firstImage.is_primary ? -1 : 1;
  });
}

async function parseJsonResponse<T>(response: Response) {
  const text = await response.text();

  if (!text) {
    return null as T | null;
  }

  return JSON.parse(text) as T;
}

function getApiErrorMessage(
  data:
    | WishlistResponseApi
    | WishlistToggleResponseApi
    | { error?: string; detail?: string; message?: string }
    | null
) {
  if (!data || typeof data !== "object") {
    return null;
  }

  if ("detail" in data && typeof data.detail === "string") {
    return data.detail;
  }

  if ("error" in data && typeof data.error === "string") {
    return data.error;
  }

  if ("message" in data && typeof data.message === "string") {
    return data.message;
  }

  return null;
}

export function normalizeWishlistResponse(data: WishlistResponseApi): NormalizedWishlist {
  const items = (data.variants ?? []).map<WishlistItem>((variant) => {
    const sortedImages = getSortedImages(variant.images ?? []);
    const primaryImage =
      sortedImages.find((image) => image.is_primary) ?? sortedImages[0] ?? null;
    const hoverImage =
      sortedImages.find((image) => image.order === 2) ??
      sortedImages[1] ??
      primaryImage;

    return {
      variantId: variant.id,
      title: variant.name?.trim() || variant.product_name?.trim() || variant.sku,
      slug: null,
      sku: variant.sku,
      price: parsePrice(variant.price),
      discountPrice: parsePrice(variant.discount_price || variant.price),
      discountPercentage: formatDiscountPercentage(variant.discount_percentage),
      stock: variant.stock,
      isPublic: variant.is_public,
      isNew: variant.is_new,
      image: primaryImage ? buildMediaUrl(primaryImage.file) : PRODUCT_FALLBACK_IMAGE,
      hoverImage: hoverImage ? buildMediaUrl(hoverImage.file) : PRODUCT_FALLBACK_IMAGE,
      gallery:
        sortedImages.length > 0
          ? sortedImages.map((image) => buildMediaUrl(image.file))
          : [PRODUCT_FALLBACK_IMAGE],
      optionValues: variant.option_values ?? [],
    };
  });

  return {
    items,
    updatedAt: data.updated_at ?? null,
  };
}

export async function getWishlist(accessToken?: string | null) {
  const response = await fetch("/api/wishlist", {
    method: "GET",
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
  });

  const data = await parseJsonResponse<WishlistResponseApi | { error?: string; detail?: string }>(response);

  if (!response.ok || !data || !("variants" in data)) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to load wishlist");
  }

  return normalizeWishlistResponse(data);
}

export async function toggleWishlistItem(variantId: number, accessToken?: string | null) {
  const response = await fetch("/api/wishlist", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(accessToken),
    },
    body: JSON.stringify({ variant_id: variantId }),
  });

  const data = await parseJsonResponse<
    WishlistToggleResponseApi | { detail?: string; message?: string; error?: string }
  >(response);

  if (!response.ok || !data || !("added" in data)) {
    throw new Error(getApiErrorMessage(data) ?? "Failed to update wishlist");
  }

  return data as WishlistToggleResponseApi;
}
